import { Box, CircularProgress } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { PersistGate } from 'redux-persist/integration/react';
import AllEvents from 'src/pages/all-events/index';
import DeveloperPage from 'src/pages/developer';
import GigsByDay from 'src/pages/gigs-by-day/index';
import SingleEventPage from 'src/pages/single-event/index';
import SingleEventClients from 'src/pages/single-event/clients/index';
import SingleEventLayout from 'src/pages/single-event/SingleEventLayout';
import TemplateEditor from 'src/pages/template-editor';
import ClientFormEditor from 'src/pages/client-form-editor';
import App from './App';
import { singleEventLoader } from './loaders/activeEventLoaders';
import { allEventsLoader } from './loaders/eventsLoaders';
import AccessDenied from './pages/no-access';
import SignIn from './pages/signin';
import { persistor, store } from './store';
import { queryClient } from './utils/queryClient';
import Signup from 'src/pages/signup';
import ForgotPassword from 'src/pages/forgot-password';
import { RoleBasedRoutes } from './utils/RoleBasedRoutes';
import VerificationRequest from './pages/no-access/VerificationRequest';
import StaffLayout from './layouts/StaffLayout';
import TalentLayout from './layouts/TalentLayout';
import ClientLayout from './layouts/ClientLayout';
import { ProtectedRoute } from './utils/ProtectedRoute';
import TalentPage from 'src/pages/talent-view';
import ClientPage from 'src/pages/client-view';
import ClientFormSummaryPage from 'src/pages/client-view/components/ClientFormSummaryPage';
import PrivacyPolicyTerms from 'src/pages/policy/PrivacyPolicyTerms';
import Connect from './pages/connect';

const authRoutes = [
  {
    path: '/sign-in',
    Component: SignIn
  },
  {
    path: '/sign-up',
    Component: Signup
  },
  {
    path: '/forgot-password',
    Component: ForgotPassword
  },
  {
    path: '/no-access',
    Component: AccessDenied
  },
  {
    path: '/no-access/verify',
    Component: VerificationRequest
  },
  {
    path: '/privacy-policy-terms',
    Component: PrivacyPolicyTerms
  }
];

const router = createBrowserRouter([
  {
    path: '/connect',
    Component: Connect
  },
  {
    Component: App,
    children: [
      ...authRoutes,

      {
        path: '/',
        element: (
          <RoleBasedRoutes
            staffRoutes={<Navigate to="/staff" replace />}
            talentRoutes={<Navigate to="/dashboard" replace />}
            clientRoutes={<Navigate to="/client" replace />}
          />
        )
      },

      {
        element: (
          <ProtectedRoute requiredRole="Staff">
            <StaffLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'staff',
            Component: AllEvents,
            loader: allEventsLoader,
            shouldRevalidate: () => false
          },
          {
            path: 'gigs-by-day',
            Component: GigsByDay
          },
          {
            path: 'event',
            Component: SingleEventLayout,
            loader: singleEventLoader,
            shouldRevalidate: ({ currentUrl, nextUrl }) =>
              currentUrl.searchParams.get('id') !== nextUrl.searchParams.get('id'),
            children: [
              {
                path: 'talent',
                Component: SingleEventPage
              },
              {
                path: 'clients',
                Component: SingleEventClients
              }
            ]
          },
          {
            path: 'template-editor',
            Component: TemplateEditor
          },
          {
            path: 'client-form-editor',
            Component: ClientFormEditor
          },
          {
            path: 'developer',
            Component: DeveloperPage
          }
        ]
      },

      {
        element: (
          <ProtectedRoute requiredRole="Talent">
            <TalentLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'dashboard',
            element: <TalentPage />
          }
        ]
      },

      {
        element: (
          <ProtectedRoute requiredRole="Client">
            <ClientLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'client',
            element: <ClientPage />
          },
          {
            path: 'client/form-summary/:eventId',
            element: <ClientFormSummaryPage />
          }
        ]
      }
    ]
  }
]);

const PersistLoading = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}
  >
    <CircularProgress color="secondary" />
  </Box>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    <PersistGate loading={<PersistLoading />} persistor={persistor} onBeforeLift={() => {}}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </PersistGate>
  </Provider>
);
